import React, { useState } from 'react';
import { Box, Typography, Divider, IconButton, Dialog, DialogTitle, SnackbarOrigin } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import { useAddSkillMutation, useDeleteSkillMutation, useUpdateSkillMutation } from '../api/skillApi';
import { useAddLanguageMutation, useDeleteLanguageMutation } from '../api/languageApi';
import SkillForm from './SkillForm';
import CloseIcon from '@mui/icons-material/Close';
import AutohideSnackbar from './SnackBar';


interface SkillsProps {
  isLoggedIn: boolean;
  userId: number;
  skills:Skill[];
  languages: Language[]
}

export interface Skill {
  id?: number;
  name: string;
  category?: {
    id: number;
    name: string;
  }
}

export interface Language {
  id: number;
  name: string;
  proficiency: string;
}

interface CreateSkillResponse {

    endpointName: string;
    isSuccess: boolean;
    data: {
      message: string;
    }
  }

  interface State extends SnackbarOrigin {
    open: boolean
  }

const Skills: React.FC<SkillsProps> = ({ isLoggedIn, userId, skills, languages }) => {
  const { t } = useTranslation();

  // const { data: skills = [], error, isLoading } = useGetSkillsByUserIdQuery(userId);
  // const { data: languages = [] } = useGetLanguagesByUserIdQuery(userId)
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [createSkill, response] = useAddSkillMutation<CreateSkillResponse>({fixedCacheKey: 'shared-skills'});
  const [updateSkill] = useUpdateSkillMutation<CreateSkillResponse>({fixedCacheKey: 'shared-skills'});
  const [removeSkill] = useDeleteSkillMutation<CreateSkillResponse>({fixedCacheKey: 'shared-skills'});
  const [addLanguage, addLanguageResponse] = useAddLanguageMutation({fixedCacheKey: 'shared-language'});
  const [deleteLanguage] = useDeleteLanguageMutation({fixedCacheKey: 'shared-language'});
  const [state, setState] = React.useState<State>({
		open: false,
		vertical: 'bottom',
		horizontal: 'center',
	});

  // if (isLoading) return <p>Loading...</p>;
  // if (error) return <p>Error loading skills</p>;

  console.log('response', response)
  console.log('addLanguageResponse', addLanguageResponse)

  const groupByCategory = (skills: Skill[]) => {
    return skills.reduce((acc, skill) => {
      const categoryName = `${skill.category?.name}`
      if (!acc[categoryName]) {
        acc[categoryName] = {
          skill: [] 
        }
      }
      acc[categoryName].skill.push(skill);
      return acc
    }, {} as { [categoryName: string]: {skill: Skill[]}} )
  }

  const groupedSkills = groupByCategory(skills)
  
  console.log('skills', groupedSkills )

  const handleEdit = () => {
    setIsModalOpen(true);
  }

  const handleClose = () => {
    setIsModalOpen(false);
  };

  const handleCreateSkill = async (newSkillData: { name: string; categoryId: number; userId: number }) => {
    console.log('newSkillData', newSkillData)
    try {
      await createSkill(newSkillData).unwrap();
      setState((newState) => ({
        ...newState, 
        open: true,
        vertical: 'bottom',
        horizontal: 'center',
      }))
    } catch (error) {
      console.error('Error creating skill:', error);
    }
  };

  const handleUpdateSkill = async (updatedSkills: { id?: number; name: string; categoryId: number; }[]) => {
    console.log('updatedSkills', updatedSkills)
    try {
      // Send each skill update request
      for (const skill of updatedSkills) {
        console.log('Updating skill with payload:', skill);
        console.log('typeof', typeof skill.id)
        await updateSkill(skill).unwrap();
      }
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error updating skills:', error);
    }
  };

  const handleRemoveSkill = async (skillId: number | undefined) => {
    if (skillId === undefined) return;
    try {
      await removeSkill(skillId).unwrap(); // Make API call to delete the skill
      setState((newState) => ({
        ...newState, 
        open: true,
        vertical: 'bottom',
        horizontal: 'center',
      }))
    } catch (error) {
      console.error('Error removing skill:', error);
    }
  };

  const handleAddLanguage = async (language: { name: string; proficiency: string, userId: number }) => {
    try {
      await addLanguage(language).unwrap();
      setState((newState) => ({
        ...newState,
        open: true,
        vertical: 'bottom',
        horizontal: 'center',
      }));
    } catch (error) {
      console.error('Error adding language:', error);
    }
  };

  const handleRemoveLanguage = async (languageId?: number) => {
    try {
      await deleteLanguage(languageId).unwrap();
      setState((newState) => ({
        ...newState,
        open: true,
        vertical: 'bottom',
        horizontal: 'center',
      }));
    } catch (error) {
      console.error('Error deleting language:', error);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: '#3c4043',
        padding: 4,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: 4,
        maxHeight: 672,
      }}
    >
      <AutohideSnackbar
        message={
          response?.endpointName === "addSkill" ?
          response?.isSuccess
          ? 'Skill added successfully' 
          : 'Skill added failed'
          : response.endpointName === "deleteSkill" 
          ? response?.isSuccess
          ? 'Skill removed succefully'
          : 'Skill removing failed'
          : addLanguageResponse.endpointName === "addLanguage" 
          ? addLanguageResponse?.isSuccess
          ? 'Language added sucessfully'
          : 'Language added failed'
          : 'Langage removed successfully'
        }
        severity={response.isSuccess ? 'success' : 'error'}
        state={state} 
				setState={setState}
				vertical={state.vertical}
				horizontal={state.horizontal}
      />
      {/* Title in the Top-Left Corner */}
      <Typography
        variant="h4"
        color="white"
        sx={{
          position: 'absolute',
          top: 8,
          left: 16,
        }}
      >
        {t('skills')}
      </Typography>
      
      <Divider
        sx={{
          marginTop: '30px',
          width: '110%',
          backgroundColor: 'white',
          alignSelf: 'center',
        }}
      />
      {isLoggedIn && (
        <IconButton
          onClick={handleEdit}
          sx={{
            position: 'absolute',
            top: 8,
            right: 16,
            color: 'white',
            backgroundColor: '#282c34'
          }}
        >
          <EditIcon />
        </IconButton>
      )}
      {/* Section Content */}
      <Box sx={{ mt: 4, color: 'white', textAlign: 'left', width: '90%' }}>
        {Object.entries(groupedSkills).map(([category, { skill }]) => (
          <Box key={category}>
            <Typography variant="h6">{category}</Typography>
            <Typography variant="body2">
            {skill.map(s => s.name).join(', ')}
            </Typography>
            <Divider sx={{ my: 2, backgroundColor: 'white' }} />
          </Box>
        ))}
        <Typography variant="h6">{t('Languages')}</Typography>
        <ul style={{position: 'relative', left: -28}}>
          {languages.map((language) => (
            <Typography variant="body2" key={language.id} sx={{marginTop: 1.5}}>
                <li>{`${language.name} : ${language.proficiency}`}</li>
            </Typography>
          ))}
          </ul>
      </Box>
     
      {/* Edit Modal */}
      <Dialog open={isModalOpen} onClose={handleClose}>
        <Box
          sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          paddingBottom: 0,
        }}
        >        
          <DialogTitle>{t('Edit Skill')}</DialogTitle>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
        </Box>
        <Divider variant='middle' />
        <SkillForm 
          skills={groupedSkills}
          languages={languages}
          onCreateLanguage={handleAddLanguage}
          onRemoveLanguage={handleRemoveLanguage}
          onUpdateSkill={handleUpdateSkill}
          onCreateSkill={handleCreateSkill}
          onRemoveSkill={handleRemoveSkill}
          handleClose={handleClose} 
        />
      </Dialog>
    </Box>
  );
};

export default Skills;
